import axios from 'axios';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

interface Admin {
    id: string;
    username: string;
}

interface AuthStore {
    token: string | null;
    admin: Admin | null;
    isAuthenticated: boolean;
    login: (username: string, password: string) => Promise<void>;
    logout: () => void;
}

const apiUrl = process.env.NEXT_PUBLIC_API_URL;
const AUTH_COOKIE_NAME = 'admin-auth-storage';
const AUTH_COOKIE_MAX_AGE = 60 * 60 * 24 * 7; // 7 days

// Helper to set auth cookie
const setAuthCookie = (token: string) => {
    if (typeof document !== 'undefined') {
        document.cookie = `${AUTH_COOKIE_NAME}=${encodeURIComponent(token)}; path=/; max-age=${AUTH_COOKIE_MAX_AGE}`;
    }
};

// Helper to clear auth cookie
const clearAuthCookie = () => {
    if (typeof document !== 'undefined') {
        document.cookie = `${AUTH_COOKIE_NAME}=; path=/; max-age=0`;
    }
};

export const useAuthStore = create<AuthStore>()(
    persist(
        (set) => ({
            token: null,
            admin: null,
            isAuthenticated: false,
            login: async (username: string, password: string) => {
                const response = await axios.post(`${apiUrl}/auth/login`, {
                    username,
                    password,
                });

                const { token, admin } = response.data;

                set({
                    token,
                    admin,
                    isAuthenticated: true,
                });

                // Set default auth header for axios
                axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

                // Set cookie for middleware
                setAuthCookie(token);
            },
            logout: () => {
                set({
                    token: null,
                    admin: null,
                    isAuthenticated: false,
                });
                delete axios.defaults.headers.common['Authorization'];
                clearAuthCookie();
            },
        }),
        {
            name: 'admin-auth-storage',
            storage: createJSONStorage(() => localStorage),
            onRehydrateStorage: () => (state) => {
                if (state?.token) {
                    axios.defaults.headers.common['Authorization'] = `Bearer ${state.token}`;
                    // Also restore cookie on rehydrate
                    setAuthCookie(state.token);
                } else {
                    // No token - make sure cookie is cleared
                    clearAuthCookie();
                }
            },
        }
    )
);
